/**
 * ConceptFieldCategorySection — generic, config-driven category branch under a Concept, sibling
 * to Units/Development Metrics (ConceptDevMetricsSection.jsx). All fields live directly on the
 * Project Concept record (tblaAwLsCi6XGsdFY) — reads from ctx.conceptCache, writes via
 * services/design.js#updateConcept. One component + a per-category field config (below) powers
 * Demolition, Grading & Paving, Pools and Spas, and Site Scope — built to the same
 * shape/behavior as ConceptDevMetricsSection (tree row + zoom, per-field ⋯ Edit, no
 * category-level menu action), verified field-by-field against the live Airtable schema (no
 * guessed field names/types).
 *
 * Field kinds (schema-driven, not guessed):
 *   - 'number' fields (sf/ft/count/gal) are user-entered, each with its own ⋯ Edit action.
 *   - 'formula' fields (pct/text) are computed by Airtable — display only.
 *   - 'multipleLookupValues' fields are read-only reference values (e.g. Proposed Building
 *     Exterior Fixture Count — a real schema quirk: unlike its "Existing" counterpart, this one
 *     is a lookup, not a plain number, on the live table).
 *   - 'singleSelect'/'multipleSelects' fields are user-entered via a dropdown / checkbox-list
 *     edit modal (Pool or Spa Configuration; Existing Structures to be Removed; Site Scope
 *     Items; Exterior Material Changes).
 *
 * Renders in two modes — tree (default, full header) vs zoomMode (children only, used for
 * sec:concept:<conceptId>:<suffix>). No category-level menu action in either mode, matching
 * Development Metrics.
 */
import { useState, useEffect, useReducer } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getFirst } from '../outline/utils.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'
import { updateConcept } from '../services/design.js'

/* ── Per-category field configs — verified against the live Airtable schema ─────────────────── */

export const DEMOLITION_FIELDS = [
  { label: 'Total Building Demolition Area',   field: 'Total Building Demolition Area',   kind: 'sf', editable: true },
  { label: 'Existing Roof Area',               field: 'Existing Roof Area',               kind: 'sf', editable: true },
  { label: 'Demo Roof Area',                   field: 'Demo Roof Area',                   kind: 'sf', editable: true },
  { label: 'Roof Demo Percentage',             field: 'Roof Demo Percentage',             kind: 'pct',  computed: true },
  { label: 'Minor Remodel Roof',               field: 'Minor Remodel Roof',               kind: 'text', computed: true },
  { label: 'Existing Exterior Walls',          field: 'Existing Exterior Walls',           kind: 'ft', editable: true },
  { label: 'Demo Exterior Walls',              field: 'Demo Exterior Walls',               kind: 'ft', editable: true },
  { label: 'Exterior Wall Demo Percentage',    field: 'Exterior Wall Demo Percentage',     kind: 'pct',  computed: true },
  { label: 'Minor Remodel Exterior Walls',     field: 'Minor Remodel Exterior Walls',      kind: 'text', computed: true },
  { label: 'Existing Interior Walls',          field: 'Existing Interior Walls',           kind: 'ft', editable: true },
  { label: 'Demo Interior Walls',              field: 'Demo Interior Walls',               kind: 'ft', editable: true },
  { label: 'Existing Ext and Int Walls',       field: 'Existing Ext and Int Walls',        kind: 'ft',   computed: true },
  { label: 'Demo Ext and Int Walls',           field: 'Demo Ext and Int Walls',            kind: 'ft',   computed: true },
  { label: 'Ext and Int Walls Demo Percentage',field: 'Ext and Int Walls Demo Percentage', kind: 'pct',  computed: true },
  { label: 'Existing Foundations',             field: 'Existing Foundations',              kind: 'ft', editable: true },
  { label: 'Demo Foundations',                 field: 'Demo Foundations',                  kind: 'ft', editable: true },
  { label: 'Foundations Demo Percentage',      field: 'Foundations Demo Percentage',       kind: 'pct',  computed: true },
  { label: 'Combined Demo Percentage',         field: 'Combined Demo Percentage',          kind: 'pct',  computed: true },
  { label: 'Combined Demo Message',            field: 'Combined Demo Message',             kind: 'text', computed: true },
  { label: 'Minor Remodel Combined',           field: 'Minor Remodel Combined',            kind: 'text', computed: true },
]

export const GRADING_PAVING_FIELDS = [
  { label: 'Existing Impervious Structure SF',      field: 'Existing Impervious Structure SF',      kind: 'sf',  editable: true },
  { label: 'Existing Impervious Structure Percent', field: 'Existing Impervious Structure Percent', kind: 'pct', computed: true },
  { label: 'Proposed Impervious Structure SF',      field: 'Proposed Impervious Structure SF',      kind: 'sf',  editable: true },
  { label: 'Proposed Impervious Structure Percent', field: 'Proposed Impervious Structure Percent', kind: 'pct', computed: true },
  { label: 'Existing Impervious Hardscape SF',      field: 'Existing Impervious Hardscape SF',      kind: 'sf',  editable: true },
  { label: 'Existing Impervious Hardscape Percent', field: 'Existing Impervious Hardscape Percent', kind: 'pct', computed: true },
  { label: 'Proposed Impervious Hardscape SF',      field: 'Proposed Impervious Hardscape SF',      kind: 'sf',  editable: true },
  { label: 'Proposed Impervious Hardscape Percent', field: 'Proposed Impervious Hardscape Percent', kind: 'pct', computed: true },
  { label: 'Cut Inside Building Footprint',   field: 'Cut Inside Building Footprint',   kind: 'cy', editable: true },
  { label: 'Cut Outside Building Footprint',  field: 'Cut Outside Building Footprint',  kind: 'cy', editable: true },
  { label: 'Cut Pool Spa',                    field: 'Cut Pool Spa',                    kind: 'cy', editable: true },
  { label: 'Total Cut',                       field: 'Total Cut',                       kind: 'cy', computed: true },
  { label: 'Fill Inside Building Footprint',  field: 'Fill Inside Building Footprint',  kind: 'cy', editable: true },
  { label: 'Fill Outside Building Footprint', field: 'Fill Outside Building Footprint', kind: 'cy', editable: true },
  { label: 'Fill Pool Spa',                   field: 'Fill Pool Spa',                   kind: 'cy', editable: true },
  { label: 'Total Fill',                      field: 'Total Fill',                      kind: 'cy', computed: true },
  { label: 'Net Export Inside Building Footprint',  field: 'Net Export Inside Building Footprint',  kind: 'cy', computed: true },
  { label: 'Net Export Outside Building Footprint', field: 'Net Export Outside Building Footprint', kind: 'cy', computed: true },
  { label: 'Net Export Pool Spa',                   field: 'Net Export Pool Spa',                   kind: 'cy', computed: true },
  { label: 'Total Net Export',                      field: 'Total Net Export',                      kind: 'cy', computed: true },
]

export const POOLS_SPAS_FIELDS = [
  { label: 'Pool or Spa Configuration', field: 'Pool or Spa Configuration', kind: 'select', editable: true,
    choices: ['Spa Only', 'Pool Only - Small', 'Pool Only - Medium', 'Pool Only - Large', 'Pool and Spa - Small', 'Pool and Spa - Medium', 'Pool and Spa - Large'] },
  { label: 'Pool Length', field: 'Pool Length', kind: 'ft', editable: true },
  { label: 'Pool Width',  field: 'Pool Width',  kind: 'ft', editable: true },
  { label: 'Pool Depth',  field: 'Pool Depth',  kind: 'ft', editable: true },
  { label: 'Pool Volume', field: 'Pool Volume', kind: 'gal', computed: true },
  { label: 'Pool Volume Override', field: 'Pool Volume Override', kind: 'gal', editable: true },
  { label: 'Spa Length', field: 'Spa Length', kind: 'ft', editable: true },
  { label: 'Spa Width',  field: 'Spa Width',  kind: 'ft', editable: true },
  { label: 'Spa Depth',  field: 'Spa Depth',  kind: 'ft', editable: true },
  { label: 'Spa Volume', field: 'Spa Volume', kind: 'gal', computed: true },
  { label: 'Spa Volume Override', field: 'Spa Volume Override', kind: 'gal', editable: true },
]

// Note: Proposed Landscaped Open Space / Percent / Conforms live under Development Metrics
// already (ConceptDevMetricsSection's METRIC_FIELDS) — deliberately not duplicated here.
export const SITE_SCOPE_FIELDS = [
  { label: 'Existing Egress Doors', field: 'Existing Egress Doors', kind: 'count', editable: true },
  { label: 'Proposed Egress Doors', field: 'Proposed Egress Doors', kind: 'count', editable: true },
  { label: 'Existing Building Exterior Fixture Count', field: 'Existing Building Exterior Fixture Count', kind: 'count', editable: true },
  { label: 'Proposed Building Exterior Fixture Count', field: 'Proposed Building Exterior Fixture Count', kind: 'count', reference: true },
  { label: 'Existing Landscape Fixture Count', field: 'Existing Landscape Fixture Count', kind: 'count', editable: true },
  { label: 'Proposed Landscape Fixture Count', field: 'Proposed Landscape Fixture Count', kind: 'count', editable: true },
  { label: 'New AC Units', field: 'New AC Units', kind: 'count', editable: true },
  { label: 'Existing Structures to be Removed', field: 'Existing Structures to be Removed', kind: 'multiselect', editable: true,
    choices: ['Residence', 'Detached Garage', 'ADU', 'Shed'] },
  { label: 'Existing Structures Remove List', field: 'Existing Structures Remove List', kind: 'text', computed: true },
  { label: 'Site Scope Items', field: 'Site Scope Items', kind: 'multiselect', editable: true,
    choices: ['Remove Existing Structures', 'New Patios', 'New Walkways', 'New Driveways', 'New Flagstone Paving', 'New Landscape', 'New Pool', 'New Spa'] },
  { label: 'Site Scope Items List', field: 'Site Scope Items List', kind: 'text', computed: true },
  { label: 'Exterior Material Changes', field: 'Exterior Material Changes', kind: 'multiselect', editable: true,
    choices: ['Replace Window(s)', 'Replace Door(s)', 'Replace Exterior Sidings - Style Change', 'Replace Exterior Sidings - In Kind', 'Replace Roofing - Style Change', 'Replace Roofing - In Kind'] },
  { label: 'Site Scope Combined List', field: 'Site Scope Combined List', kind: 'text', computed: true },
]

// suffix (the sec:concept:<id>:<suffix> zoom key segment) → { title, fields }. Shared by outline.js
// (breadcrumb + zoomTo), DesignSection.jsx / MilestoneSections.jsx (mount + render), ZoomTitle.jsx.
export const CONCEPT_CATEGORIES = {
  demolition: { title: 'Demolition',       fields: DEMOLITION_FIELDS },
  grading:    { title: 'Grading & Paving', fields: GRADING_PAVING_FIELDS },
  pools:      { title: 'Pools and Spas',   fields: POOLS_SPAS_FIELDS },
  sitescope:  { title: 'Site Scope',       fields: SITE_SCOPE_FIELDS },
}

// Airtable returns formula results as an object rather than a number/string when the formula
// hits an error or indeterminate case (typically a divide-by-zero from blank input fields) —
// e.g. {specialValue: 'NaN'} for a percentage formula, {error: '#ERROR!'} for a field that
// concatenates another errored field (confirmed live: Roof Demo Percentage / Combined Demo
// Message on a fresh concept with blank Existing/Demo areas). Treat both shapes as blank rather
// than falling through to String(v), which renders the literal text "[object Object]".
function isAirtableFormulaError(v) { return !!v && typeof v === 'object' && !Array.isArray(v) && ('error' in v || 'specialValue' in v) }

function isBlank(v) { return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0) || isAirtableFormulaError(v) }

function fmtValue(raw, kind) {
  if (kind === 'multiselect') return isBlank(raw) ? '—' : (Array.isArray(raw) ? raw.join(', ') : String(raw))
  const v = getFirst(raw)
  if (isBlank(v)) return '—'
  if (kind === 'text' || kind === 'select') return String(v)
  const n = typeof v === 'number' ? v : parseFloat(v)
  if (Number.isNaN(n)) return String(v)
  if (kind === 'pct')   return (n * 100).toFixed(2) + '%'
  if (kind === 'sf')    return n.toFixed(2) + ' SF'
  if (kind === 'ft')    return n.toFixed(2) + ' ft'
  if (kind === 'cy')    return n.toFixed(2) + ' CY'
  if (kind === 'gal')   return Math.round(n).toLocaleString() + ' gal'
  if (kind === 'count') return String(Math.round(n))
  return String(n)
}

function FieldOriginTag({ text }) {
  return (
    <span style={{
      fontFamily: '"DM Sans",sans-serif', fontSize: 9, fontStyle: 'italic', textTransform: 'uppercase',
      letterSpacing: '0.04em', color: 'var(--aj-text-muted)', marginLeft: 6,
    }}>
      {text}
    </span>
  )
}

function FieldRow({ f, fields, onEdit, dataKey }) {
  const value = fields[f.field]
  const empty = isBlank(f.kind === 'multiselect' ? value : getFirst(value))
  const menu = useActionMenu()
  return (
    <div className="wf-node wf-node--item" data-key={dataKey}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{f.label}</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: empty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
          }}>
            {fmtValue(value, f.kind)}
          </span>
          {f.computed && <FieldOriginTag text="Computed" />}
          {f.reference && <FieldOriginTag text="Reference" />}
        </div>
        {f.editable && <ActionMenuButton menu={menu} />}
      </div>
      {f.editable && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onEdit(f) }}>Edit</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/* ── FieldEditModal — single-field edit form; branches on kind (number / select / multiselect) ── */
function FieldEditModal({ field, concept, onSave, onClose }) {
  const isMulti = field.kind === 'multiselect'
  const isSelect = field.kind === 'select'
  const isNumeric = !isMulti && !isSelect
  const raw = concept.fields[field.field]
  const [value, setValue] = useState(() => (isNumeric ? (getFirst(raw) ?? '') : (isMulti ? '' : (getFirst(raw) ?? ''))))
  const [checked, setChecked] = useState(() => new Set(isMulti && Array.isArray(raw) ? raw : []))
  const [saving, setSaving] = useState(false)

  function toggle(choice) {
    setChecked(prev => { const next = new Set(prev); if (next.has(choice)) next.delete(choice); else next.add(choice); return next })
  }

  async function handleSave() {
    setSaving(true)
    try {
      let out
      if (isMulti) out = [...checked]
      else if (isSelect) out = value === '' ? null : value
      else out = value === '' ? null : parseFloat(value)
      await onSave(field.field, out)
      onClose()
    } catch {
      setSaving(false)
    }
  }

  const inp = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none', width: '100%', boxSizing: 'border-box' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 420, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit {field.label}
        </div>

        {isMulti ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
            {(field.choices || []).map(c => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' }}>
                <input type="checkbox" checked={checked.has(c)} onChange={() => toggle(c)} />
                {c}
              </label>
            ))}
          </div>
        ) : isSelect ? (
          <select autoFocus value={value} onChange={e => setValue(e.target.value)} style={inp}>
            <option value="">— None —</option>
            {(field.choices || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, color: 'var(--aj-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {field.kind === 'sf' ? 'Square feet' : field.kind === 'ft' ? 'Feet' : field.kind === 'cy' ? 'Cubic yards' : field.kind === 'gal' ? 'Gallons' : field.kind === 'count' ? 'Count' : ''}
            </div>
            <input autoFocus type="number" step="any" value={value} onChange={e => setValue(e.target.value)} style={inp} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * @param {string} conceptId
 * @param {string} suffix — one of CONCEPT_CATEGORIES' keys ('demolition' | 'grading' | 'pools' | 'sitescope')
 * @param {boolean} [zoomMode]
 */
export default function ConceptFieldCategorySection({ conceptId, suffix, zoomMode }) {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const [editingField, setEditingField] = useState(null)
  const cat = CONCEPT_CATEGORIES[suffix]
  const key = 'sec:concept:' + conceptId + ':' + suffix
  const isCollapsed = zoomMode ? false : (wf.collapsed[key] !== false)

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  const concept = (ctx.conceptCache || []).find(c => c.id === conceptId)

  async function handleSave(fieldName, value) {
    const updated = await updateConcept(conceptId, { [fieldName]: value })
    if (Array.isArray(ctx.conceptCache)) {
      const idx = ctx.conceptCache.findIndex(c => c.id === conceptId)
      if (idx !== -1) ctx.conceptCache[idx] = { ...ctx.conceptCache[idx], fields: { ...ctx.conceptCache[idx].fields, ...updated.fields } }
    }
    ctx.render()
  }

  if (!cat) return null

  const content = !concept ? (
    <div className="wf-loading">Loading…</div>
  ) : (
    <>
      {cat.fields.map(f => <FieldRow key={f.field} dataKey={'catfield:' + conceptId + ':' + suffix + ':' + f.field} f={f} fields={concept.fields} onEdit={setEditingField} />)}
    </>
  )

  const editModal = editingField && concept && (
    <FieldEditModal field={editingField} concept={concept} onSave={handleSave} onClose={() => setEditingField(null)} />
  )

  if (zoomMode) {
    return (
      <div className="wf-list">
        {content}
        {editModal}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--cat" title={'Focus on ' + cat.title}
          data-fn="zoomTo" data-level="concept-category" data-conceptid={conceptId} data-category={suffix} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">{cat.title}</span>
        </div>
      </div>
      {!isCollapsed && <div className="wf-children">{content}</div>}
      {editModal}
    </div>
  )
}
